import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface FutureSelfSpotlightProps {
  identityStatement?: string;
  currentPivotalStep?: string;
}

export const FutureSelfSpotlight: React.FC<FutureSelfSpotlightProps> = ({
  identityStatement,
  currentPivotalStep
}) => {
  const navigate = useNavigate();

  if (!identityStatement) {
    return (
      <Card className="relative border-0 bg-card/30 backdrop-blur-sm border-dashed border border-primary/20 overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-accent/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7 text-accent" />
            </div>
            <div className="flex-1 space-y-3">
              <h3 className="text-lg font-light text-foreground tracking-wide">Define Your Future Self</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Discover who you're becoming and align your daily actions with your highest vision.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/future-self-navigator')}
              variant="outline"
              className="group border-primary/30 hover:bg-primary/10 font-light"
            >
              Begin Journey
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative border-0 bg-gradient-to-br from-accent/10 via-primary/5 to-secondary/5 shadow-lg overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-accent via-primary to-accent/50" />
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-radial from-accent/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <CardContent className="p-6 sm:p-8 pl-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-accent" />
            </div>
            <span className="text-xs font-light text-muted-foreground tracking-widest uppercase">Your Future Self</span>
          </div>

          {/* Identity Statement */}
          <div className="relative pl-4">
            <Quote className="w-6 h-6 text-accent/30 absolute -left-2 -top-2" />
            <p className="text-xl sm:text-2xl font-extralight text-foreground italic leading-relaxed">
              {identityStatement}
            </p>
          </div>

          {/* Current Focus */}
          {currentPivotalStep && (
            <div className="bg-card/30 rounded-xl p-4 space-y-2">
              <p className="text-xs text-muted-foreground font-light tracking-wide uppercase">Current Focus</p>
              <p className="text-sm text-foreground font-light">
                {currentPivotalStep}
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="pt-2">
            <Button 
              onClick={() => navigate('/future-self-navigator')}
              variant="ghost"
              className="group hover:bg-accent/10 font-light"
            >
              <span>Continue Your Journey</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};